import { Component, Input, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { AdminService, LanguageService, Language } from "@mypxplat/xplat/core";
import { AppService } from "@mypxplat/xplat/web/core";

type LangStatus = "idle" | "translating" | "done" | "error";

/** admin_config `type` under which the shared "do not translate" term list is persisted online. */
const DO_NOT_TRANSLATE_CONFIG_TYPE = "ai_do_not_translate";

/** Brand/product terms prefilled into the Auto-translate config so the AI keeps them verbatim. */
const DEFAULT_DO_NOT_TRANSLATE = "Fender, PreSonus, Squier, Studio One, Notion, AudioBox, FaderPort, Quantum, StudioLive, Eris, Sceptre, ATOM, Revelator, Temblor, PreSonus Sphere";

/**
 * Reusable admin editor for authoring per-locale product descriptions, backed by the
 * `product_metadata` DynamoDB table (keyed by Product.stringId for PreSonus, PIM productNo for
 * Fender). Admins write English in the unified field, click "Auto-translate" to fill the other
 * supported locales via AI, review, and Save. Only the `descriptions` / `shortDescriptions`
 * attributes are written (an upsert/merge on the backend), so other metadata is left untouched.
 *
 * Auto-translate fires one request per language in parallel (each a short Lambda call, avoiding
 * timeouts) and shows per-language progress pills. A "Configure Auto-translate" panel lets admins
 * edit a comma-separated list of terms the AI must keep verbatim (brand/product names).
 *
 * If a product has legacy copy only in its MySQL Product.description (no product_metadata entry
 * yet), that text seeds the English field so Auto-translate + Save migrates it into product_metadata.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-localized-descriptions-editor",
  templateUrl: "localized-descriptions-editor.component.html",
  standalone: false,
})
export class LocalizedDescriptionsEditorComponent implements OnInit {
  /** The metadata key: Product.stringId (PreSonus) or PIM productNo (Fender). */
  @Input() metadataKey: string;
  /** Existing per-locale long-description map, if any. */
  @Input() descriptions: Record<string, string> = {};
  /** Existing per-locale short-description map, if any. */
  @Input() shortDescriptions: Record<string, string> = {};
  /** Legacy English source (MySQL/PIM) used to seed + migrate the `en` entry when none exists yet. */
  @Input() englishDescription: string = "";
  @Input() englishShortDescription: string = "";

  /** Non-English locales, hidden behind the "Show all languages" toggle. */
  public otherLanguages: Language[] = [];
  public showAllLanguages = false;
  /** Long-description edit mode (Visual Quill vs raw HTML), toggled by the pills. */
  public descriptionContentType: "visual" | "html" = "visual";
  /**
   * The Quill editor only ingests its `content` on init, so after Auto-translate rewrites the maps
   * we briefly drop this flag to force Angular to recreate the visual editors with the new copy.
   */
  public quillReady = true;
  public translating = false;
  public saving = false;
  public saved = false;
  /** Set when the admin edits/auto-translates but hasn't saved yet; lets a parent Save flush it. */
  public dirty = false;
  public error: string | null = null;
  /** True when any supported non-English locale is missing a translated description. */
  public missingTranslations = false;
  /** Per-language translation progress, keyed by locale code. */
  public langStatus: Record<string, LangStatus> = {};
  /** Auto-translate configuration panel. */
  public showConfig = false;
  public savingConfig = false;
  public doNotTranslateTerms = DEFAULT_DO_NOT_TRANSLATE;

  constructor(
    private adminService: AdminService,
    private languageService: LanguageService,
    private appService: AppService
  ) {}

  ngOnInit() {
    this.otherLanguages = this.languageService.languages.filter((lang) => lang.code !== "en");
    // Work on our own copies so nothing is mutated until the admin saves.
    this.descriptions = { ...(this.descriptions || {}) };
    this.shortDescriptions = { ...(this.shortDescriptions || {}) };
    // Migrate legacy English copy into the unified field if this product has no metadata entry yet.
    if (!this.descriptions.en && this.englishDescription) this.descriptions.en = this.englishDescription;
    if (!this.shortDescriptions.en && this.englishShortDescription) this.shortDescriptions.en = this.englishShortDescription;
    this.otherLanguages.forEach((lang) => (this.langStatus[lang.code] = "idle"));
    this.loadDoNotTranslateTerms();
    this.recomputeMissing();
  }

  /** Recomputes the "missing translations" flag: any non-en locale lacking a description. */
  recomputeMissing() {
    this.missingTranslations = this.otherLanguages.some((lang) => !this.descriptions[lang.code]);
  }

  /** Marks the editor dirty (unsaved edits) and refreshes the missing-translations flag. */
  markDirty() {
    this.dirty = true;
    this.recomputeMissing();
  }

  /**
   * Saves only if there are unsaved edits and a metadata key is present. Lets the host screen's
   * product "Save" button flush pending description edits so admins don't have to remember to click
   * "Save descriptions" separately.
   */
  saveIfDirty() {
    if (this.dirty && this.metadataKey) this.save();
  }

  /** Loads the shared do-not-translate list from admin_config, falling back to brand defaults. */
  private loadDoNotTranslateTerms() {
    this.adminService.get(`config/${DO_NOT_TRANSLATE_CONFIG_TYPE}`).subscribe({
      next: (result: any) => {
        if (Array.isArray(result?.data) && result.data.length) this.doNotTranslateTerms = result.data.join(", ");
      },
      error: () => {
        /* no stored config yet or fetch failed — keep brand defaults */
      },
    });
  }

  /** Persists the edited term list to admin_config (shared across admins) and closes the panel. */
  saveConfig() {
    this.savingConfig = true;
    this.adminService.put(`config/${DO_NOT_TRANSLATE_CONFIG_TYPE}`, { data: this.parseDoNotTranslate() }).subscribe({
      next: () => {
        this.savingConfig = false;
        this.showConfig = false;
      },
      error: (err) => {
        this.savingConfig = false;
        this.appService.alert(this.appService.helperService.retrieveErrorMessage(err) || "Failed to save configuration.");
      },
    });
  }

  private parseDoNotTranslate(): string[] {
    return (this.doNotTranslateTerms || "")
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  }

  /**
   * Translates the English copy into every other supported locale, one request per language in
   * parallel, updating each language's progress pill as it resolves.
   */
  autoTranslate() {
    const description = this.descriptions.en || "";
    const shortDescription = this.shortDescriptions.en || "";
    if (!description && !shortDescription) {
      this.appService.alert("Enter the English description first, then auto-translate.");
      return;
    }

    this.error = null;
    this.translating = true;
    const doNotTranslate = this.parseDoNotTranslate();
    let remaining = this.otherLanguages.length;

    this.otherLanguages.forEach((lang) => {
      this.langStatus[lang.code] = "translating";
      this.adminService
        .post(`product/metadata/${this.metadataKey}/translate`, {
          fields: { description, shortDescription },
          targetLang: lang.code,
          doNotTranslate,
        })
        .subscribe({
          next: (result: any) => {
            if (result?.description?.[lang.code] !== undefined) this.descriptions = { ...this.descriptions, [lang.code]: result.description[lang.code] };
            if (result?.shortDescription?.[lang.code] !== undefined) this.shortDescriptions = { ...this.shortDescriptions, [lang.code]: result.shortDescription[lang.code] };
            this.dirty = true;
            this.langStatus[lang.code] = "done";
            this.finishLang(--remaining);
          },
          error: () => {
            this.langStatus[lang.code] = "error";
            this.finishLang(--remaining);
          },
        });
    });
  }

  /** Called as each language request settles; wraps up once all have resolved. */
  private finishLang(remaining: number) {
    this.recomputeMissing();
    if (remaining > 0) return;
    this.translating = false;
    if (this.otherLanguages.some((lang) => this.langStatus[lang.code] === "error")) {
      this.error = "Some languages failed to translate. Retry Auto-translate to fill them in.";
    }
    // Force the Quill editors to re-ingest the freshly translated HTML.
    this.quillReady = false;
    setTimeout(() => (this.quillReady = true));
  }

  /** Persists the two locale maps to product_metadata (merged; other attributes untouched). */
  save() {
    if (!this.metadataKey) {
      this.appService.alert("This product has no string ID / productNo, so descriptions can't be saved.");
      return;
    }
    this.saving = true;
    this.error = null;
    this.adminService.put(`product/metadata/${this.metadataKey}`, { descriptions: this.descriptions, shortDescriptions: this.shortDescriptions }).subscribe({
      next: () => {
        this.saving = false;
        this.saved = true;
        this.dirty = false;
        setTimeout(() => (this.saved = false), 2000);
      },
      error: (err) => {
        this.saving = false;
        this.error = "Save failed. Please try again.";
        this.appService.alert(this.appService.helperService.retrieveErrorMessage(err) || this.error);
      },
    });
  }
}
